// action - state management
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import AuthApi from 'api/authApi';
import UserApi from 'api/userApi';
import { ACCOUNT_STATUSES } from 'constants/accountConstants';
import { parseRawUser } from 'services/userServices';
import { SNACKBAR_OPEN } from 'store/actions';
import { getIdNumber } from 'utils/create-profile';

// ===========================|| AUTH SLICE ||=========================== //

export const login = createAsyncThunk('auth/login', async (data, { rejectWithValue, dispatch }) => {
    try {
        const response = await AuthApi.login({
            email: data.email,
            password: data.password
        });
        const { authtoken, ...user } = response.data;

        if (user.status !== ACCOUNT_STATUSES.accepted && user.status !== ACCOUNT_STATUSES.notActivated) {
            return rejectWithValue({ status: 'Not Allowed' });
        }

        return { token: authtoken, user: parseRawUser(user) };
    } catch (err) {
        if (err.response) {
            if (err.response.status !== 401) {
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: err.response.data.detail,
                    variant: 'alert',
                    alertSeverity: 'error'
                });
            }

            return rejectWithValue({ message: err.response.data.detail });
        }

        return rejectWithValue({ message: err.message });
    }
});

export const getUserInfo = createAsyncThunk('auth/getUserInfo', async (data, { rejectWithValue, getState, dispatch }) => {
    try {
        const { account } = getState();
        const response = await UserApi.getUserInfo(account.user?.id);

        return parseRawUser(response.data);
    } catch (err) {
        if (err.response) {
            if (err.response.status !== 401) {
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: err.response.data.detail,
                    variant: 'alert',
                    alertSeverity: 'error'
                });
            }

            return rejectWithValue({ message: err.response.data.detail });
        }

        dispatch({
            type: SNACKBAR_OPEN,
            open: true,
            message: err.message,
            variant: 'alert',
            alertSeverity: 'error'
        });

        return rejectWithValue({ message: err.message });
    }
});

export const activateAccount = createAsyncThunk('auth/activateAccount', async (data, { rejectWithValue, getState, dispatch }) => {
    try {
        const { account } = getState();

        const mobileNumber = data.phoneNumberCode?.phone + data.phoneNumberWithoutCode;

        const response = await UserApi.activateAccount(account.user?.id, {
            first_name: data.firstName,
            second_first_name: data.secondName,
            first_last_name: data.lastName,
            second_last_name: data.secondLastName,
            birth_day: data.birth.day.toString(),
            birth_month: (+data.birth.month + 1).toString(),
            birth_year: data.birth.year.toString(),
            gender: data.gender,
            marital_status: data.maritalStatus,
            mobile_number: mobileNumber,
            other_phone_number: mobileNumber,
            user_country_id: data.country?.id.toString(),
            user_city_id: data.cityName ? 0 : data.city?.id.toString(),
            user_city_name: data.cityName,
            user_address: data.address,
            document_type_id: data.idType?.id.toString(),
            document_number: getIdNumber(data),
            tax_id: data.taxId,
            bank_country_id: data.bankCountry?.id.toString(),
            bank_city_id: data.bankCity ? data.bankCity.id.toString() : '',
            bank_id: data.bankName ? 0 : data.bank?.id.toString(),
            bank_name: data.bankName,
            bank_account_type: data.accountType,
            bank_account_number: data.accountNumber,
            bank_cci: data.bankCCI,
            bank_branch: data.bankBranch,
            user_postal_code: data.postalCode,
            email: data.email
        });

        if (response.data.status !== 'success') {
            return rejectWithValue({ message: response.data.message });
        }

        return { status: 'success', user: parseRawUser(response.data.user) };
    } catch (err) {
        if (err.response) {
            if (err.response.status !== 401) {
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: err.response.data.detail,
                    variant: 'alert',
                    alertSeverity: 'error'
                });
            }
            return rejectWithValue({ message: err.response.data.detail });
        }

        dispatch({
            type: SNACKBAR_OPEN,
            open: true,
            message: err.message,
            variant: 'alert',
            alertSeverity: 'error'
        });

        return rejectWithValue({ message: err.message });
    }
});

export const createPassword = createAsyncThunk('auth/createPassword', async (data, { rejectWithValue, dispatch }) => {
    try {
        const response = await AuthApi.createPassword({
            password_1: data.password,
            password_2: data.confirmPassword,
            saltcode: data.key
        });
        const { authtoken, ...user } = response.data;

        if (user.status !== ACCOUNT_STATUSES.accepted && user.status !== ACCOUNT_STATUSES.notActivated) {
            return rejectWithValue({ status: 'Not Allowed' });
        }

        return { token: authtoken, user: parseRawUser(user) };
    } catch (err) {
        if (err.response) {
            // passwordCreate0002 - if key for password create or reset is expired or invalid
            if (err.response.status !== 401 && err.response.data?.error !== 'passwordCreate0002') {
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: err.response.data.detail,
                    variant: 'alert',
                    alertSeverity: 'error'
                });
            }

            return rejectWithValue({ message: err.response.data.detail, code: err.response.data.error });
        }

        return rejectWithValue({ message: err.message });
    }
});

const initialState = {
    user: {},
    isAuth: false,
    token: '',
    isLoading: false,
    isProfileActive: false,
    isProfileActiveError: false
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.isAuth = initialState.isAuth;
            state.token = initialState.token;
            state.user = initialState.user;
        }
    },
    extraReducers: {
        [login.fulfilled]: (state, action) => {
            const { token, user } = action.payload;
            state.isAuth = true;
            state.token = token;
            state.user = user;
            state.isProfileActive = state.user.status === ACCOUNT_STATUSES.accepted || state.user.status === ACCOUNT_STATUSES.notActivated;
        },
        [login.rejected]: (state, action) => {
            if (action.payload.status === 'Not Allowed') {
                state.isProfileActive = false;
                state.isProfileActiveError = true;
            }
        },
        [getUserInfo.pending]: (state) => {
            state.isLoading = true;
        },
        [getUserInfo.fulfilled]: (state, action) => {
            state.user = { ...state.user, ...action.payload };
            state.isLoading = false;

            const isProfileActive = state.user.status === ACCOUNT_STATUSES.accepted || state.user.status === ACCOUNT_STATUSES.notActivated; // for other status account is not active and user cannot login
            state.isProfileActive = isProfileActive;

            if (!isProfileActive) {
                state.isProfileActiveError = true;
            }
        },
        [getUserInfo.rejected]: (state) => {
            state.isLoading = false;
        },
        [activateAccount.fulfilled]: (state, action) => {
            state.user = action.payload.user;
        },
        [createPassword.fulfilled]: (state, action) => {
            const { token, user } = action.payload;
            state.isAuth = true;
            state.token = token;
            state.user = user;
            state.isProfileActive = state.user.status === ACCOUNT_STATUSES.accepted || state.user.status === ACCOUNT_STATUSES.notActivated;
        },
        [createPassword.rejected]: (state, action) => {
            if (action.payload.status === 'Not Allowed') {
                state.isProfileActive = false;
                state.isProfileActiveError = true;
            }
        }
    }
});

export const { logout } = authSlice.actions;

export default authSlice.reducer;
